import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { FilePathPipe } from 'src/app/shared/pipes/file-path.pipe';
import { MatTelInput } from 'mat-tel-input';
import {
  PublicBookingService,
  PublicProduct,
  PublicPriceRow,
  PublicSchool,
} from '../../services/public-booking.service';

// ─── View models (derived from the public products response) ─────────────────

interface ActivityCard {
  activityId: string;
  name: string;
  fromPrice: number;
  colorIndex: number;
  imageUrl: string | null;
}

interface ModalityCard {
  modalityId: string;
  name: string;
}

interface LevelCard {
  levelId: string;
  name: string;
  position: number;
}

/** One bookable option = a product combined with one of its price tiers. */
interface PackageOption {
  productId: string;
  priceRowId: string;
  title: string;
  levelName: string | null; // set when the tier is level-specific
  durationLabel: string;
  price: number;
  publicDescription: string | null;
  minutes: number;
}

// PRODUCT_DURATION_UNIT: 1=Hour 2=Day 3=Week 4=Minute
const UNIT_MINUTES: Record<number, number> = { 1: 60, 2: 1440, 3: 10080, 4: 1 };
function durationMinutesOf(row: PublicPriceRow): number {
  return row.durationValue * (UNIT_MINUTES[row.durationUnit] ?? 1);
}

function durationLabelOf(row: PublicPriceRow): string {
  const v = row.durationValue;
  switch (row.durationUnit) {
    case 1: return `${v} h`;
    case 2: return v === 1 ? '1 day' : `${v} days`;
    case 3: return v === 1 ? '1 week' : `${v} weeks`;
    case 4: return `${v} min`;
    default: return `${v}`;
  }
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

@Component({
  selector: 'app-enroll',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatSelectModule,
    FilePathPipe,
    MatTelInput,
  ],
  // European day/month/year (16/10/2026); the native adapter follows this locale.
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  templateUrl: './enroll.component.html',
  styleUrl: './enroll.component.scss',
})
export class EnrollComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private publicBookingService = inject(PublicBookingService);

  schoolId = '';
  school: PublicSchool | null = null;
  allProducts: PublicProduct[] = [];

  loading = true;
  loadError = false;
  submitting = false;

  // ── Flow state ──────────────────────────────────────────────────────────────
  step: Step = 1;
  selectedActivityId: string | null = null;
  selectedModalityId: string | null = null;
  selectedLevelId: string | null = null;
  selectedProductId: string | null = null;
  selectedPriceRowId: string | null = null;

  // Step 3 — stay dates & preferred time
  readonly today = new Date();
  arrivalDateObj: Date | null = null;
  departureDateObj: Date | null = null;
  preferredTime = ''; // HH:mm, or '' = no preference (school decides)

  /** Preferred start times offered in the dropdown, 30-min steps 07:00–21:00. */
  readonly timeSlots: string[] = Array.from({ length: 29 }, (_, i) => {
    const mins = 7 * 60 + i * 30;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  /** Why the last request failed, shown on the step the customer must fix. */
  submitError = '';

  // Step 4 — customer details
  firstName = '';
  lastName = '';
  email = '';
  phone = '';

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.schoolId = this.route.snapshot.paramMap.get('schoolId') ?? '';
    this.loadProducts();
  }

  loadProducts(): void {
    this.loading = true;
    this.loadError = false;
    this.publicBookingService.listProducts(this.schoolId).subscribe({
      next: (res) => {
        this.school = res.school;
        // Only products with at least one price tier are bookable.
        this.allProducts = (res.products || []).filter((p) => p.prices?.length);
        this.loading = false;
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  // ── Derived lists ─────────────────────────────────────────────────────────────

  private minPriceOf(p: PublicProduct): number {
    return Math.min(...(p.prices || []).map((r) => Number(r.price)));
  }

  get activities(): ActivityCard[] {
    const seen = new Map<string, ActivityCard>();
    for (const p of this.allProducts) {
      const id = p.activityId;
      const existing = seen.get(id);
      const fromPrice = this.minPriceOf(p);
      if (existing) {
        existing.fromPrice = Math.min(existing.fromPrice, fromPrice);
        if (!existing.imageUrl && p.publicImageUrl) existing.imageUrl = p.publicImageUrl;
      } else {
        seen.set(id, {
          activityId: id,
          name: p.activity?.name || 'Activity',
          fromPrice,
          colorIndex: seen.size % 4,
          imageUrl: p.publicImageUrl || null,
        });
      }
    }
    return Array.from(seen.values());
  }

  get activityProducts(): PublicProduct[] {
    return this.allProducts.filter((p) => p.activityId === this.selectedActivityId);
  }

  /** Distinct modalities available within the selected activity. */
  get modalities(): ModalityCard[] {
    const seen = new Map<string, ModalityCard>();
    for (const p of this.activityProducts) {
      if (p.modalityId && !seen.has(p.modalityId)) {
        seen.set(p.modalityId, { modalityId: p.modalityId, name: p.modality?.modalityName || 'Modality' });
      }
    }
    return Array.from(seen.values());
  }

  private get modalityProducts(): PublicProduct[] {
    return this.activityProducts.filter((p) => p.modalityId === this.selectedModalityId);
  }

  /** Union of level assignments across products of the selected activity+modality. */
  get levels(): LevelCard[] {
    const seen = new Map<string, LevelCard>();
    for (const p of this.modalityProducts) {
      for (const l of p.levels ?? []) {
        if (!seen.has(l.id)) seen.set(l.id, { levelId: l.id, name: l.levelName, position: l.position ?? Number.MAX_SAFE_INTEGER });
      }
    }
    // The school's own order (Settings > Levels), lowest first.
    return Array.from(seen.values()).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  }

  /** True once the level step is resolved (picked, or no levels exist in scope). */
  get levelSelectionSatisfied(): boolean {
    return this.levels.length === 0 || !!this.selectedLevelId;
  }

  /**
   * Bookable options: products matching the filters × their visible price
   * tiers (global tiers + tiers of the selected level), mirroring the
   * internal product-selector semantics.
   */
  get packageOptions(): PackageOption[] {
    if (!this.selectedModalityId || !this.levelSelectionSatisfied) return [];
    const options: PackageOption[] = [];
    for (const p of this.modalityProducts) {
      // When a level is picked, the product must be assigned to it.
      if (this.selectedLevelId && !(p.levels ?? []).some((l) => l.id === this.selectedLevelId)) continue;
      for (const row of p.prices ?? []) {
        if (this.selectedLevelId && row.levelId && row.levelId !== this.selectedLevelId) continue;
        options.push({
          productId: p.id,
          priceRowId: row.id,
          title: p.title,
          levelName: row.levelId ? row.level?.levelName || null : null,
          durationLabel: durationLabelOf(row),
          price: Number(row.price),
          publicDescription: p.publicDescription,
          minutes: durationMinutesOf(row),
        });
      }
    }
    // Shortest package first (2 h, 4 h, 6 h…), then cheapest.
    return options.sort((a, b) => a.minutes - b.minutes || a.price - b.price);
  }

  get currentActivity(): ActivityCard | undefined {
    return this.activities.find((a) => a.activityId === this.selectedActivityId);
  }

  get selectedProduct(): PublicProduct | undefined {
    return this.allProducts.find((p) => p.id === this.selectedProductId);
  }

  get selectedPackage(): PackageOption | undefined {
    return this.packageOptions.find((o) => o.priceRowId === this.selectedPriceRowId);
  }

  /** Cover image for the product page: selected product → activity representative → none. */
  get coverImageUrl(): string | null {
    return this.selectedProduct?.publicImageUrl || this.currentActivity?.imageUrl || null;
  }

  // ── Navigation ────────────────────────────────────────────────────────────────

  selectActivity(id: string): void {
    this.selectedActivityId = id;
    this.selectedModalityId = null;
    this.selectedLevelId = null;
    this.clearPackage();
    this.step = 2;

    // Auto-select when there's only one modality (then cascade to level/package).
    const mods = this.modalities;
    if (mods.length === 1) this.onSelectModality(mods[0].modalityId);
  }

  onSelectModality(id: string): void {
    this.selectedModalityId = id;
    this.selectedLevelId = null;
    this.clearPackage();

    const lvls = this.levels;
    if (lvls.length === 1) this.onSelectLevel(lvls[0].levelId);
  }

  onSelectLevel(id: string): void {
    this.selectedLevelId = id;
    this.clearPackage();

    const opts = this.packageOptions;
    if (opts.length === 1) this.selectPackage(opts[0]);
  }

  selectPackage(option: PackageOption): void {
    this.selectedProductId = option.productId;
    this.selectedPriceRowId = option.priceRowId;
  }

  private clearPackage(): void {
    this.selectedProductId = null;
    this.selectedPriceRowId = null;
  }

  goToDateStep(): void {
    if (!this.selectedPriceRowId) return;
    this.step = 3;
  }

  /** A stay is valid once both dates are set and departure is not before arrival. */
  get stayValid(): boolean {
    if (!this.arrivalDateObj || !this.departureDateObj) return false;
    return this.departureDateObj >= this.stripTime(this.arrivalDateObj);
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Date-only "YYYY-MM-DD" in the user's local calendar, no timezone shift. */
  private toDateOnly(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Keep departure in sync when arrival moves past it. */
  onArrivalChange(): void {
    if (this.arrivalDateObj && this.departureDateObj && this.departureDateObj < this.arrivalDateObj) {
      this.departureDateObj = this.arrivalDateObj;
    }
  }

  goToDetailsStep(): void {
    if (!this.stayValid) return;
    this.submitError = '';
    this.step = 4;
  }

  goToReviewStep(): void {
    if (!this.detailsValid) return;
    this.submitError = '';
    this.step = 5;
  }

  get emailValid(): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.email.trim());
  }

  get detailsValid(): boolean {
    return !!this.firstName.trim() && this.emailValid;
  }

  /** "10 Aug 2026" for a single day, or "10 Aug → 17 Aug 2026" for a range. */
  get stayLabel(): string {
    if (!this.arrivalDateObj || !this.departureDateObj) return '';
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const a = fmt(this.arrivalDateObj);
    const d = fmt(this.departureDateObj);
    return a === d ? a : `${a} → ${d}`;
  }

  get preferredLabel(): string {
    return this.preferredTime ? this.preferredTime : 'To be arranged with the school';
  }

  get phoneDisplay(): string {
    return (this.phone || '').trim();
  }

  back(): void {
    if (this.step > 1) {
      this.step = (this.step - 1) as Step;
    }
    if (this.step === 1) {
      this.selectedActivityId = null;
      this.selectedModalityId = null;
      this.selectedLevelId = null;
      this.clearPackage();
    }
  }

  confirmBooking(): void {
    if (!this.stayValid || !this.arrivalDateObj || !this.departureDateObj) return;
    if (!this.selectedProductId || !this.selectedPriceRowId) return;
    this.submitting = true;
    this.publicBookingService
      .createBooking(this.schoolId, {
        productId: this.selectedProductId,
        priceRowId: this.selectedPriceRowId,
        levelId: this.selectedLevelId,
        arrivalDate: this.toDateOnly(this.arrivalDateObj),
        departureDate: this.toDateOnly(this.departureDateObj),
        preferredStartTime: this.preferredTime || null,
        customer: {
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          email: this.email.trim(),
          // mat-tel-input gives the full international number (+34…).
          phone: (this.phone || '').trim() || null,
          phonePrefix: null,
        },
      })
      .subscribe({
        next: () => {
          this.submitError = '';
          this.step = 6;
          this.submitting = false;
        },
        error: (err) => {
          // Go back to the step that holds the problem and say what it is,
          // instead of always sending the customer to the dates.
          const message: string = err?.error?.message || 'We could not send your request. Please try again.';
          const m = message.toLowerCase();
          this.submitError = message;
          if (/email|first ?name|last ?name|phone|customer/.test(m)) this.step = 4;
          else if (/date|arrival|departure|time/.test(m)) this.step = 3;
          this.submitting = false;
        },
      });
  }

  resetFlow(): void {
    this.step = 1;
    this.selectedActivityId = null;
    this.selectedModalityId = null;
    this.selectedLevelId = null;
    this.clearPackage();
    this.arrivalDateObj = null;
    this.departureDateObj = null;
    this.preferredTime = '';
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.loadProducts();
  }
}
